import { fabric } from 'fabric';

import { currentCrop, readShape, shapeClip } from './crop';

/*
 * Safe ways to handle a picture that is already on the page (lessons BUG-28, BUG-29, BUG-30 in tests/lessons.test.ts):
 *   swapSrc         point it at another file — the new file is loaded and checked FIRST, the picture changes only
 *                   when it opened (Fabric's own setSrc empties the picture before it knows the file failed); the
 *                   kept part (ครอป) and its shape come along — Fabric's setElement resets the size to the whole file
 *   applyShape      remake the circle/rounded clipPath from `photoShape` for the picture's current kept part
 *   peekOriginal    "ดูต้นฉบับ" (held): draw the picture without its แต่งภาพ look — only what is drawn changes
 *   releaseTextures free the full-size GPU copy Fabric keeps for a picture with filters (แต่งภาพ); called whenever a
 *                   picture leaves the canvas — deleting it, and every undo/redo/page change (loadFromJSON clears all)
 */

type Pic = fabric.Image & { photoShape?: unknown; _originalElement?: HTMLImageElement | HTMLCanvasElement };

/** the picture file's own size (before any crop) */
export function naturalSize(img: fabric.Image): { w: number; h: number } {
  const el = (img as Pic)._originalElement ?? (img.getElement() as HTMLImageElement);
  return { w: (el as HTMLImageElement)?.naturalWidth || el?.width || 0, h: (el as HTMLImageElement)?.naturalHeight || el?.height || 0 };
}

/** the circle/rounded frame of a cropped picture, made again from its saved shape (none for a plain rectangle) */
export function applyShape(img: fabric.Image) {
  const spec = shapeClip(readShape((img as Pic).photoShape), img.width ?? 0, img.height ?? 0);
  if (!spec) { img.clipPath = undefined; img.dirty = true; return; }
  const common = { left: 0, top: 0, originX: 'center' as const, originY: 'center' as const };
  img.clipPath = spec.type === 'ellipse'
    ? new fabric.Ellipse({ ...common, rx: spec.rx, ry: spec.ry })
    : new fabric.Rect({ ...common, width: spec.width, height: spec.height, rx: spec.rx, ry: spec.ry });
  img.dirty = true;
}

/**
 * Load `url`, and only when it really opened put it in the picture; rejects with a Thai reason otherwise.
 * `k` = the new file's size ÷ the old one's (1 = the same picture from another address, < 1 = made smaller): the
 * kept part is moved to the new file's pixels; the caller divides the scale by `k` so it stays the same size on the page.
 * `from` = the address the picture had when this was asked: if it got another file while loading, nothing is changed
 * and the result is false (the new file was made for the old one) (BUG-31).
 * `sameSize`: the new file must be exactly as big as the old one (ลบพื้นหลัง / คืนพื้นหลัง: the crop is kept as it is).
 */
export function swapSrc(img: fabric.Image, url: string, why = 'เปิดรูปใหม่ไม่ได้', k = 1, from = img.getSrc(), sameSize = false): Promise<boolean> {
  return new Promise((res, rej) => {
    fabric.util.loadImage(url, (el: HTMLImageElement | null, failed?: boolean) => {
      if (failed || !el || !(el.naturalWidth > 0)) { rej(new Error(`${why} (โหลดไฟล์ไม่สำเร็จ รูปเดิมยังอยู่)`)); return; }
      if (img.getSrc() !== from) { res(false); return; }
      if (sameSize) {
        const was = naturalSize(img);
        if (el.naturalWidth !== was.w || el.naturalHeight !== was.h) {
          rej(new Error(`${why} (รูปใหม่ ${el.naturalWidth}×${el.naturalHeight} px ไม่เท่ารูปเดิม ${was.w}×${was.h} px รูปเดิมยังอยู่)`));
          return;
        }
      }
      try {
        // the kept part as it is NOW: the user may have cropped while the file was loading (BUG-31)
        const nat = naturalSize(img);
        const c = currentCrop(img, nat.w, nat.h);
        // same as setSrc after a good load, keeping the kept part (setElement alone resets it to the whole file)
        // (rounded into the new file: a source area past its edge draws nothing on Safari)
        const W = el.naturalWidth, H = el.naturalHeight;
        const x = Math.min(c.x * k, W - 1), y = Math.min(c.y * k, H - 1);
        img.setElement(el, { cropX: x, cropY: y, width: Math.min(c.w * k, W - x), height: Math.min(c.h * k, H - y) });
        applyShape(img);
        img.setCoords();
        res(true);
      } catch (e) {
        rej(new Error(`${why} (${e instanceof Error ? e.message : String(e)})`));
      }
    }, undefined, (img as fabric.Image & { crossOrigin?: string }).crossOrigin ?? undefined);
  });
}

type Peek = fabric.Image & { _element?: HTMLImageElement | HTMLCanvasElement; _originalElement?: HTMLImageElement | HTMLCanvasElement; _filteredEl?: HTMLCanvasElement | null; __peek?: boolean };

/**
 * Why ดูต้นฉบับ cannot start for this object, or null. Checked before the press shows anything, so the reason is what
 * the person reads (BUG-45).
 */
export function peekProblem(o: fabric.Object | null, edited: boolean): string | null {
  if (!o) return 'เลือกรูปก่อน แล้วกดค้างเพื่อดูต้นฉบับ';
  if (o.type === 'activeSelection') return 'ดูต้นฉบับได้ทีละรูป เลือกรูปเดียวก่อน';
  if (o.type !== 'image') return 'ดูต้นฉบับใช้กับรูปภาพเท่านั้น';
  if (!edited) return 'รูปนี้ยังไม่ได้แต่ง ที่เห็นอยู่คือต้นฉบับ';
  const p = o as Peek;
  if (!p._originalElement) return 'รูปยังโหลดไม่เสร็จ รอสักครู่แล้วลองใหม่';
  if (!p.__peek && p._element === p._originalElement) return 'รูปนี้แสดงแบบไม่แต่งอยู่แล้ว (เครื่องนี้ยังวาดการแต่งภาพไม่ได้) ลองเปิดแต่งภาพอีกครั้ง';
  return null;
}

/** the quick-tap hint ("กดค้างไว้…") only when the press really showed the original — never over a reason (BUG-45) */
export const tapHint = (started: boolean, heldMs: number) => started && heldMs < 350;

/** true while the picture is drawn as its original (the "ต้นฉบับ" mark on the page says so) */
export const isPeeking = (o: fabric.Object) => !!(o as Peek).__peek;

/**
 * "ดูต้นฉบับ": `on` draws the picture from its original file (no filters); off draws its look again from its filters.
 * Nothing that is saved changes — photoEdit, filters and the file stay (the saved JSON reads the file from
 * _originalElement), so it is never recorded or saved as unedited. Returns false when there was nothing to do.
 */
export function peekOriginal(img: fabric.Image, on: boolean): boolean {
  const p = img as Peek;
  if (on) {
    if (p.__peek || !p._originalElement || p._element === p._originalElement) return false;
    p.__peek = true;
    p._element = p._originalElement;
  } else {
    if (!p.__peek) return false;
    p.__peek = false;
    // drawn again from the filters into the canvas it already has: Fabric's applyFilters makes a NEW full-size canvas
    // when _element is the file (up to 64 MB per press on an iPad, freed late — BUG-46)
    if (p._filteredEl) p._element = p._filteredEl;
    try { img.applyFilters(); } catch { /* drawn as the original until the next change; the saved look is untouched */ }
  }
  img.dirty = true;
  return true;
}

type Backend = { evictCachesForKey?: (k: string) => void };
/** forget the GPU copies Fabric keeps for this picture (they are made again if it is drawn with filters later) */
export function releaseTextures(o: fabric.Object) {
  const key = (o as fabric.Image & { cacheKey?: string }).cacheKey;
  const b = (fabric as unknown as { filterBackend?: Backend }).filterBackend;
  if (o.type !== 'image' || !key || !b?.evictCachesForKey) return;
  b.evictCachesForKey(key);
  b.evictCachesForKey(`${key}_filtered`);
}

/**
 * Fabric makes its GPU filter canvas once (on the first picture with filters) and never watches it. When the device
 * takes the GPU back (memory pressure on an iPad) every picture with filters is drawn blank, exports included, with
 * no error. `cb` runs after Fabric has been switched to its CPU filters, so it can draw the pictures again.
 * Checked on each call (the GPU canvas only exists after the first filter); safe to call often.
 */
const watched = new WeakSet<object>();
let lostHandler: (() => void) | null = null;
export function onGpuLost(cb: () => void) { lostHandler = cb; }
export function watchGpu() {
  const f = fabric as unknown as { filterBackend?: { canvas?: HTMLCanvasElement }; Canvas2dFilterBackend: new () => object };
  const c = f.filterBackend?.canvas;
  if (!c || watched.has(c)) return;
  watched.add(c);
  c.addEventListener('webglcontextlost', ev => {
    ev.preventDefault();
    (f as { filterBackend?: object }).filterBackend = new f.Canvas2dFilterBackend();
    lostHandler?.();
  });
}
